import axios from "axios";
import { useRef, useState } from "react";
import { Button, Form, Spinner } from "react-bootstrap";
import { useNavigate } from "react-router-dom";
import { toast, ToastContainer } from "react-toastify";
import SideNavbar from "../../components/common/SideNavbar";
import TopNavbar from "../../components/common/TopNavbar";
import GenericCrudPanel from "../../components/common/crud/GenericCrudPanel";

const HR_BASE = `${process.env.REACT_APP_BACKEND_API_URL}/hr`;

const ASSET_MODULE = {
  key: "asset",
  title: "Assets",
  endpoint: "/asset/",
  fields: [
    { name: "asset_code", label: "Asset Code", disabled: true },
    { name: "asset_type", label: "Asset Type", required: true, type: "select", optionsKey: "assetTypeOptions" },
    { name: "asset_name", label: "Asset Name", required: true },
    { name: "status", label: "Status", required: true, type: "select", optionsKey: "assetStatusOptions" },
    { name: "assigned_to", label: "Assigned To", type: "select", optionsKey: "employeeOptions" },
    { name: "condition", label: "Condition" },
    { name: "serial_number", label: "Serial Number" },
    { name: "brand", label: "Brand" },
    { name: "model", label: "Model" },
    { name: "purchase_date", label: "Purchase Date", type: "date" },
    { name: "purchase_cost", label: "Purchase Cost", type: "number", step: "0.01" },
    { name: "warranty_expiry", label: "Warranty Expiry", type: "date" },
    { name: "vendor", label: "Vendor" },
    { name: "remarks", label: "Remarks", type: "textarea" },
  ],
  columns: [
    {
      name: "Asset Code",
      selector: (row) => row.asset_code || "-",
      sortable: true,
      filterKey: "asset_code",
      filterWidth: "120px",
    },
    {
      name: "Asset Name",
      selector: (row) => row.asset_name || "-",
      sortable: true,
      filterKey: "asset_name",
      filterWidth: "150px",
    },
    {
      name: "Type",
      selector: (row) => row.asset_type_data?.name || "-",
      sortable: true,
      filterKey: "asset_type",
      filterWidth: "130px",
      filterValue: (row) => row.asset_type_data?.name || "",
    },
    {
      name: "Status",
      selector: (row) => row.status || "-",
      sortable: true,
      filterKey: "status",
      filterWidth: "120px",
    },
    {
      name: "Assigned To",
      selector: (row) =>
        row.assigned_to_data?.employee_code ||
        row.assigned_to_data?.user_data?.name ||
        "-",
      sortable: true,
      filterKey: "assigned_to",
      filterWidth: "150px",
      filterValue: (row) =>
        row.assigned_to_data?.employee_code ||
        row.assigned_to_data?.user_data?.name ||
        "",
    },
  ],
  initialForm: {
    asset_code: "",
    asset_type: "",
    asset_name: "",
    status: "available",
    assigned_to: "",
    serial_number: "",
    brand: "",
    model: "",
    purchase_date: "",
    purchase_cost: "",
    warranty_expiry: "",
    vendor: "",
    remarks: "",
  },
};

const ASSET_STATUS_OPTIONS = [
  { value: "available", label: "Available" },
  { value: "assigned", label: "Assigned" },
  { value: "repair", label: "Repair" },
  { value: "maintenance", label: "Maintenance" },
  { value: "reserved", label: "Reserved" },
  { value: "lost", label: "Lost" },
  { value: "scrapped", label: "Scrapped" },
];

async function loadOptions() {
  const [assetTypes, employees] = await Promise.all([
    axios.get(`${HR_BASE}/assettype/`),
    axios.get(`${HR_BASE}/employee/`),
  ]);
  return {
    assetTypeOptions: (assetTypes.data || []).map((item) => ({ value: item.id, label: item.name || item.id })),
    employeeOptions: (employees.data || []).map((item) => ({ value: item.id, label: `${item.employee_code} - ${item.user_data?.name || ""}` })),
    assetStatusOptions: ASSET_STATUS_OPTIONS,
  };
}

function AssetManagement() {
  const navigate = useNavigate();
  const [menuDrawerShow, setMenuDrawerShow] = useState(true);
  const [dataFromTopNavBar, setDataFromTopNavBar] = useState("");

  const [actionLoading, setActionLoading] = useState(false);
  const [assetSummary, setAssetSummary] = useState(null);
  const [showSummaryModal, setShowSummaryModal] = useState(false);
  const [showStatusModal, setShowStatusModal] = useState(false);
  const [statusRecord, setStatusRecord] = useState(null);
  const [targetStatus, setTargetStatus] = useState("");
  const reloadRef = useRef(() => { });

  const handleDataFromTopNavBar = (permissiondata) => {
    setDataFromTopNavBar(permissiondata);
  };

  const handleLoadAssetSummary = async () => {
    try {
      setActionLoading(true);
      const response = await axios.get(`${HR_BASE}/asset/summary/`);
      setAssetSummary(response.data);
      setShowSummaryModal(true);
    } catch (err) {
      toast.error(err?.response?.data?.error || "Error fetching asset summary.");
    } finally {
      setActionLoading(false);
    }
  };

  const openStatusModal = (row, reload) => {
    reloadRef.current = reload;
    setStatusRecord(row);
    setTargetStatus(row.status || "");
    setShowStatusModal(true);
  };

  const submitStatusUpdate = async () => {
    if (!statusRecord?.id || !targetStatus) {
      toast.error("Status is required.");
      return;
    }
    try {
      setActionLoading(true);
      await axios.put(`${HR_BASE}/asset/${statusRecord.id}/status/`, { status: targetStatus });
      toast.success("Asset status updated successfully.");
      setShowStatusModal(false);
      reloadRef.current();
    } catch (err) {
      toast.error(err?.response?.data?.error || "Error updating asset status.");
    } finally {
      setActionLoading(false);
    }
  };

  const renderExtraToolbarActions = () => (
    <Button className="btn btn-sm btn-info border-0 px-3 mx-2" onClick={handleLoadAssetSummary}>
      {actionLoading ? <Spinner as="span" animation="border" size="sm" /> : <i className="fa fa-bar-chart"></i>} Summary
    </Button>
  );

  const renderExtraRowActions = (row, module, reload) => (
    <>
      <Button className="btn btn-sm btn-secondary border-0 mx-1" title="View Details" onClick={() => navigate(`/hr/asset/${row.id}`)}>
        <i className="fa fa-eye"></i>
      </Button>
      <Button className="btn btn-sm btn-warning border-0 mx-1" title="Update Status" onClick={() => openStatusModal(row, reload)}>
        <i className="fa fa-refresh"></i>
      </Button>
    </>
  );

  return (
    <div className="page-content-bg">
      <TopNavbar sidebarCollapsed={menuDrawerShow} topNavbarStatic={true} sendDataToParent={handleDataFromTopNavBar} />
      <SideNavbar
        sidebarCollapsed={menuDrawerShow}
        activeMenu="HR"
        activeSubMenu="AssetManagement"
        activeSubMenuLevel1=""
        permissionFromTopNavbar={dataFromTopNavBar}
      />

      <div className={menuDrawerShow ? "row main-content-collapsed text-left py-0" : "row main-content text-left py-0"}>
        <div className="col-12 page-header">
          <div className="row">
            <div className="col-sm-6 py-1">
              <Button className="btn btn-sm btn-inox-green border-0 mx-2" onClick={() => setMenuDrawerShow(!menuDrawerShow)}>
                {menuDrawerShow ? <span><i className="fa fa-chevron-left mx-1"></i></span> : <span><i className="fa fa-chevron-right mx-1"></i></span>}
              </Button>
              <span className="page-header-text">Assets</span>
            </div>
          </div>
        </div>

        <div className="col-12 text-left" style={{ marginTop: -5, paddingLeft: 20, paddingRight: 30 }}>
          <GenericCrudPanel
            module={ASSET_MODULE}
            baseUrl={HR_BASE}
            loadOptions={loadOptions}
            extraToolbarActions={renderExtraToolbarActions}
            extraRowActions={renderExtraRowActions}
          />
        </div>
      </div>

      <div className="modal fade show" style={{ display: showSummaryModal ? "block" : "none", backgroundColor: "rgba(0,0,0,0.5)" }}>
        <div className="modal-dialog modal-lg modal-dialog-centered">
          <div className="modal-content">
            <div className="modal-header">
              <h5 className="modal-title">Asset Summary</h5>
              <button type="button" className="btn-close" onClick={() => setShowSummaryModal(false)}></button>
            </div>
            <div className="modal-body">
              <div className="card border-0 shadow-sm mb-4">
                <div className="card-body text-center">
                  <h6 className="text-muted mb-2">Total Assets</h6>
                  <h2 className="fw-bold text-success mb-0">{assetSummary?.total_assets ?? 0}</h2>
                </div>
              </div>

              <div className="row">
                <div className="col-md-6 mb-3">
                  <div className="card h-100 shadow-sm">
                    <div className="card-header bg-inox-green text-white">
                      <strong>Assets by Status</strong>
                    </div>
                    <div className="card-body p-0">
                      <table className="table table-sm table-hover mb-0">
                        <thead>
                          <tr>
                            <th>Status</th>
                            <th className="text-end">Count</th>
                          </tr>
                        </thead>
                        <tbody>
                          {Object.entries(assetSummary?.by_status || {}).map(([status, count]) => (
                            <tr key={status}>
                              <td>{status}</td>
                              <td className="text-end fw-bold">{count}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                </div>

                <div className="col-md-6 mb-3">
                  <div className="card h-100 shadow-sm">
                    <div className="card-header bg-inox-green text-white">
                      <strong>Assets by Type</strong>
                    </div>
                    <div className="card-body p-0">
                      <table className="table table-sm table-hover mb-0">
                        <thead>
                          <tr>
                            <th>Type</th>
                            <th className="text-end">Count</th>
                          </tr>
                        </thead>
                        <tbody>
                          {Object.entries(assetSummary?.by_type || {}).map(([type, count]) => (
                            <tr key={type}>
                              <td>{type}</td>
                              <td className="text-end fw-bold">{count}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="modal fade show" style={{ display: showStatusModal ? "block" : "none", backgroundColor: "rgba(0,0,0,0.5)" }}>
        <div className="modal-dialog modal-dialog-centered">
          <div className="modal-content">
            <div className="modal-header">
              <h5 className="modal-title">Update Asset Status</h5>
              <button type="button" className="btn-close" onClick={() => setShowStatusModal(false)}></button>
            </div>
            <div className="modal-body">
              <Form.Select value={targetStatus} onChange={(e) => setTargetStatus(e.target.value)}>
                <option value="">Select Status</option>
                {ASSET_STATUS_OPTIONS.map((item) => (
                  <option key={item.value} value={item.value}>{item.label}</option>
                ))}
              </Form.Select>
            </div>
            <div className="modal-footer">
              <Button variant="secondary" onClick={() => setShowStatusModal(false)}>Close</Button>
              <Button className="btn-inox-green border-0" onClick={submitStatusUpdate}>
                {actionLoading ? <Spinner as="span" animation="border" size="sm" /> : "Update"}
              </Button>
            </div>
          </div>
        </div>
      </div>

      <ToastContainer />
    </div>
  );
}

export default AssetManagement;